import { complianceEntitiesApi, payeesApi } from "@api";
import { RequirementType } from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries } from "@ws-react-query";
import { useMemo } from "react";

import { queriesComplianceEntities } from "../../../../v3/query/queriesComplianceEntities";
import { queriesPayees } from "../../../../v3/query/queriesPayees";
import { getContractorComplianceEntityListRequest } from "../../../Payees/hooks/useContractorTaxCompliance";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";

import { usePayeeTaxVerificationConfig } from "./configs/payee/usePayeeTaxVerificationConfig";
import { usePayerTaxVerificationConfig } from "./configs/payer/usePayerTaxVerificationConfig";
import { TaxVerificationAdditionalData } from "./types";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const taxVerificationRequirements = requirements.filter(
    (
      requirement
    ): requirement is RequirementTableItemData & { payeeAccountId: string } =>
      requirement.type === RequirementType.TAX_VERIFICATION &&
      !!requirement.payeeAccountId
  );

  const payeeQueries = taxVerificationRequirements.map(requirement => ({
    queryKey: queriesPayees.getPayee.getQueryKey({
      payeeId: requirement.payeeAccountId
    }),
    queryFn: () => payeesApi.getPayee({ payeeId: requirement.payeeAccountId }),
    ...sharedRequirementsQueriesConfig
  }));

  // The payer-owned compliance entity per payee — its `usTaxStatus` gates the
  // W-9 "Download PDF" action (see usePayeeTaxVerificationConfig). Batched here,
  // mirroring the payee fetch, so the config reads it synchronously from cache
  // instead of calling a hook inside a per-row render callback.
  const complianceEntityQueries = taxVerificationRequirements.map(
    requirement => {
      const request = getContractorComplianceEntityListRequest(
        requirement.payeeAccountId
      );
      return {
        queryKey:
          queriesComplianceEntities.listComplianceEntities.getQueryKey(request),
        queryFn: () => complianceEntitiesApi.listComplianceEntities(request),
        ...sharedRequirementsQueriesConfig
      };
    }
  );

  return useWSQueries({
    queries: [...payeeQueries, ...complianceEntityQueries]
  });
};

/** Reads the payer-owned compliance entity for a payee synchronously from cache. */
const getComplianceEntityFromCache = (payeeId: string) =>
  queriesComplianceEntities.listComplianceEntities.getQueryData(
    getContractorComplianceEntityListRequest(payeeId)
  )?.data?.[0];

const getTaxVerificationAdditionalData = (
  requirement: RequirementTableItemData
): TaxVerificationAdditionalData => {
  const payeeId = requirement.payeeAccountId;

  return {
    payee: payeeId
      ? queriesPayees.getPayee.getQueryData({ payeeId })
      : undefined,
    complianceEntity: payeeId
      ? getComplianceEntityFromCache(payeeId)
      : undefined
  };
};

export const useTaxVerificationRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.TAX_VERIFICATION] => {
  const payerTaxVerificationConfig = usePayerTaxVerificationConfig(callback);
  const payeeTaxVerificationConfig = usePayeeTaxVerificationConfig(callback);
  const contextQueries = useContextQueries(requirements);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    () =>
      buildConfigFields<
        TaxVerificationAdditionalData,
        RequirementType.TAX_VERIFICATION
      >({
        payerConfig: payerTaxVerificationConfig,
        payeeConfig: payeeTaxVerificationConfig,
        getAdditionalData: getTaxVerificationAdditionalData,
        // Tax-verification status lives on the requirement; the batched payee and
        // compliance entity only gate the W-9 download action.
        hasDataSource: false
      })(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payerTaxVerificationConfig, payeeTaxVerificationConfig, queriesHash]
  );

  return {
    ...fields,
    getAdditionalData: getTaxVerificationAdditionalData,
    isLoading: contextQueries.some(query => query.isLoading),
    isFetching: contextQueries.some(query => query.isFetching),
    dataUpdatedAt: queriesHash
  };
};
